import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import MoreActionsMenu, { ActionDataModel } from "@common/MoreActionsMenu/MoreActionsMenu";
import { useUserContext } from "@contexts/UserContext";
import { FontSize } from "@themes/Fonts";
import { BanUserEvent, MuteUserEvent } from "../../AmplitudeRoomModerationEvents";
import {
    determineNewMutedStateForUser,
    toggleMemberBanStatus,
    toggleMemberMuteStatus,
} from "../../RoomModerationService";
import useRoomInteractingBlockedListener from "../../useRoomInteractingBlockedListener";
import useRoomMemberInfoListener from "../../useRoomMemberInfoListener";
import { ChatContextType, useChatContext } from "../ChatContext";
import { deleteMessage, reportMessage, updatePinnedMessage } from "../RoomChatService";

const StyledMoreActionsMenu = styled(MoreActionsMenu)<{
    topPosition: number;
    leftPosition: number;
    isVisible: boolean;
}>`
    position: absolute;
    top: 0;
    right: 10px;

    display: ${(props) => (props.isVisible ? "block" : "none")};

    .more-actions-menu-contents {
        z-index: 200;
        position: fixed;

        // Position menu based on where the mouse clicked
        top: calc(${(props) => props.topPosition}px - 30px);
        left: calc(${(props) => props.leftPosition}px - 150px);
        width: 140px;
    }

    .lifeat-button-text {
        font-size: ${FontSize.small};
    }

    .lifeat-button {
        border: none !important;
    }
`;

const StyledLoadingSpinner = styled.div`
    position: absolute;
    top: 5px;
    right: 15px;
`;

const MORE_ACTIONS_MENU_REPORT_MESSAGE = "Report this message";

const MORE_ACTIONS_MENU_DELETE_MESSAGE = "Delete message";

const PIN_MESSAGE = "Pin this message";
const UNPIN_MESSAGE = "Unpin this message";

const MORE_ACTIONS_MENU_BAN_USER = "Ban user";
const MORE_ACTIONS_MENU_UNBAN_USER = "Unban user";

const MORE_ACTIONS_MENU_MUTE_USER = "Mute user";
const MORE_ACTIONS_MENU_UNMUTE_USER = "Unmute user";

interface RoomModerationActionMenuProps {
    /**
     * Room ID the menu is being opened in.
     */
    readonly roomId: string;
    /**
     * User ID the menu is being opened for.
     */
    readonly targetUid: string;
    /**
     * If the user is the room host (if targetUid is the same as the current user).
     */
    readonly isHost: boolean;
    /**
     * Optional, if provided, indicates that this message is being opened from
     * the context of a chat message and will display additional actions that
     * can be performed, depending on the user accessing the menu.
     */
    readonly messageId?: string;
    /**
     * Indicates if menu should be visible
     */
    readonly isVisible: boolean;
}
export default function RoomModerationActionMenu(props: RoomModerationActionMenuProps) {
    const { roomId, targetUid, isHost, messageId } = props;

    const amplitudeService = AmplitudeService.getInstance();

    const { user } = useUserContext();
    const { pinnedMessage }: ChatContextType = useChatContext();

    // Always listen to the DB values, so we can update the UI if the values change.
    const isMutedDueToReports = useRoomInteractingBlockedListener(targetUid, roomId);
    const roomMemberInfo = useRoomMemberInfoListener(targetUid, roomId);
    const isUserMutedDb = roomMemberInfo?.isMutedByHost ?? false;
    const isUserBannedDb = roomMemberInfo?.isBannedByHost ?? false;

    const [isUserMuted, setIsUserMuted] = useState((isUserMutedDb || isMutedDueToReports) ?? false);
    const [isUserBanned, setIsUserBanned] = useState(isUserBannedDb ?? false);

    const [menuPosition, setMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsUserMuted(isUserMutedDb || isMutedDueToReports);
        setIsUserBanned(isUserBannedDb);
    }, [isUserBannedDb, isUserMutedDb, isMutedDueToReports]);

    const isCurrUserActionTarget = targetUid === user?.uid;
    const isMessagePinnable = isHost && isCurrUserActionTarget;
    const isCurrMessagePinned = messageId === pinnedMessage?.id;
    // A message is reportable if:
    // - the message is not written by the user
    // - the message is not the pinned message
    const isMessageReportable = !isCurrUserActionTarget && !isCurrMessagePinned;

    const handleReportMessage = () => {
        if (roomId && messageId && isMessageReportable) {
            reportMessage(roomId, messageId);
        }
    };

    const handleDeleteMessage = () => {
        if (roomId && messageId && isHost) {
            setIsLoading(true);
            deleteMessage(roomId, messageId).then(() => {
                setIsLoading(false);
            });
        }
    };

    const handleToggleMutedStatus = () => {
        if (isHost && !isCurrUserActionTarget) {
            setIsLoading(true);
            const [updatedStatus, updateInteractingBlockedStatus] = determineNewMutedStateForUser(
                isUserMuted,
                isMutedDueToReports,
            );

            setIsUserMuted(updatedStatus);
            toggleMemberMuteStatus(roomId, targetUid, updatedStatus, updateInteractingBlockedStatus).then(() => {
                amplitudeService.logEvent(
                    new MuteUserEvent({
                        roomId: roomId,
                        actionPerformerUid: user.uid,
                        targetUserUid: targetUid,
                    }),
                );
                setIsLoading(false);
            });
        }
    };

    const handleToggleBannedStatus = () => {
        if (isHost && !isCurrUserActionTarget) {
            setIsLoading(true);
            const updatedStatus = !isUserBanned;
            setIsUserBanned(updatedStatus);
            toggleMemberBanStatus(roomId, targetUid, updatedStatus).then(() => {
                amplitudeService.logEvent(
                    new BanUserEvent({
                        roomId: roomId,
                        actionPerformerUid: user.uid,
                        targetUserUid: targetUid,
                    }),
                );
                setIsLoading(false);
            });
        }
    };

    const handleUpdatePinnedMessage = () => {
        if (isHost && isCurrUserActionTarget) {
            const msgId = isCurrMessagePinned ? null : messageId;
            updatePinnedMessage(roomId, msgId ?? null);
        }
    };

    const actions: ActionDataModel[] = [];
    // Add actions that can be performed on a message (action menu opened from a message).
    if (messageId) {
        if (isMessageReportable) {
            actions.push({
                name: MORE_ACTIONS_MENU_REPORT_MESSAGE,
                actionHandler: handleReportMessage,
            });
        }
        if (isMessagePinnable) {
            actions.push({
                name: isCurrMessagePinned ? UNPIN_MESSAGE : PIN_MESSAGE,
                actionHandler: handleUpdatePinnedMessage,
            });
        }
        if (isHost) {
            actions.push({
                name: MORE_ACTIONS_MENU_DELETE_MESSAGE,
                actionHandler: handleDeleteMessage,
            });
        }
    }

    // Add host moderation actions. Host can't mute or ban themselves.
    if (isHost && !isCurrUserActionTarget) {
        actions.push({
            name: isUserMuted ? MORE_ACTIONS_MENU_UNMUTE_USER : MORE_ACTIONS_MENU_MUTE_USER,
            actionHandler: handleToggleMutedStatus,
        });
        actions.push({
            name: isUserBanned ? MORE_ACTIONS_MENU_UNBAN_USER : MORE_ACTIONS_MENU_BAN_USER,
            actionHandler: handleToggleBannedStatus,
        });
    }

    const loadingSpinner = (
        <StyledLoadingSpinner>
            <LoadingSpinner size={15} />
        </StyledLoadingSpinner>
    );

    const moreActionsMenu = (
        <StyledMoreActionsMenu
            className={`room-moderation-more-actions-menu`}
            actions={actions}
            topPosition={menuPosition["y"]}
            leftPosition={menuPosition["x"]}
            isVisible={props.isVisible}
        />
    );

    return actions.length > 0 ? (
        <div onClick={(event) => setMenuPosition({ x: event.clientX, y: event.clientY })}>
            {isLoading ? loadingSpinner : moreActionsMenu}
        </div>
    ) : null;
}
